/**
 * Embed Plan & Track "View whole workflow" (`references.name === previousForms`)
 * in a side pane instead of opening a new browser tab.
 */

export const PREVIOUS_FORMS_REFERENCE_NAME = "previousForms";

/** Query param that opens the PAT split pane on Regulation Details. */
export const WHOLE_WORKFLOW_QUERY_PARAM = "wholeWorkflow";

/** Details-page tab id / hash for Manual Impact Assessment. */
export const MANUAL_ASSESSMENT_SECTION_ID = "manualAssessment";

/** Query param (Fusion-safe backup for the section hash). */
export const SECTION_QUERY_PARAM = "section";

/**
 * Association id from `?wholeWorkflow=`. Rejects URLs / path junk so the
 * details page only reconstructs `{patBase}/process/{id}`.
 */
export function parseWholeWorkflowAssociationId(
  raw: string | null | undefined,
): string | null {
  const id = raw?.trim() ?? "";
  if (!id || !/^[A-Za-z0-9_-]+$/.test(id)) return null;
  return id;
}

/** Vertical splitter hit area between the details card and the workflow pane. */
export const DETAILS_WORKFLOW_SPLITTER_PX = 8;

/** Details card cannot shrink below this when the workflow pane is open. */
export const DETAILS_PANE_MIN_PX = 440;

/** Workflow pane cannot shrink below this (caps how far the details card can grow). */
export const WORKFLOW_PANE_MIN_PX = 360;

/** Workflow pane cannot exceed this share of the visible window. */
export const WORKFLOW_PANE_MAX_VIEWPORT_RATIO = 0.5;

/** First-open details share before the user drags. */
export const DETAILS_PANE_DEFAULT_RATIO = 0.5;

export function clampDetailsWorkflowPaneWidth(
  desiredPx: number,
  containerWidthPx: number,
  viewportWidthPx = containerWidthPx,
  splitterPx = DETAILS_WORKFLOW_SPLITTER_PX,
): number {
  if (containerWidthPx <= 0) return Math.max(0, Math.round(desiredPx));

  const available = Math.max(0, containerWidthPx - splitterPx);
  const workflowMaxPx = Math.floor(
    Math.max(0, viewportWidthPx) * WORKFLOW_PANE_MAX_VIEWPORT_RATIO,
  );
  const maxFromWorkflowMin = available - WORKFLOW_PANE_MIN_PX;
  const minFromWorkflowMax = available - workflowMaxPx;
  const max = Math.max(0, Math.min(available, maxFromWorkflowMin));
  const min = Math.min(Math.max(DETAILS_PANE_MIN_PX, minFromWorkflowMax), max);
  return Math.min(max, Math.max(min, Math.round(desiredPx)));
}

export function isPreviousFormsRelatedMaterial(material: {
  name?: string | null;
}): boolean {
  return (
    typeof material.name === "string" &&
    material.name.trim() === PREVIOUS_FORMS_REFERENCE_NAME
  );
}

/**
 * Keep the backend process URI; append `platformUrl` when GRI is hosted in the
 * Fusion shell so the PAT iframe can talk to the top window (same as Associate Process).
 */
export function buildWholeWorkflowIframeSrc(
  uri: string,
  platformBaseUrl = "",
): string {
  const trimmed = uri.trim();
  if (!trimmed) return trimmed;

  const platform = platformBaseUrl.trim();
  if (!platform) return trimmed;

  try {
    const url = new URL(trimmed);
    if (!url.searchParams.get("platformUrl")) {
      url.searchParams.set("platformUrl", platform);
    }
    return url.toString();
  } catch {
    return trimmed;
  }
}
